import { get, includes } from 'lodash';

import React, { useContext } from 'react';
import { UserRoles } from '../../../constants/UserRole';
import { RoutePaths } from '../../../constants/RoutePaths';
import EmployerHttpService from '../../../api/EmployerHttpService';
import TalenyaHttpService from '../../../api/TalenyaHttpService';
import Logger from '../../../services/logger';
import SettingsContext from '../../../contexts/SettingsContext';
import AuthorizationContext from '../../../contexts/AuthorizationContext';
import ModalContext from '../../../contexts/ModalContext';
import NotificationContext from '../../../contexts/NotificationContext';
import EmployerRepHttpService from '../../../api/EmployerRepHttpService';
import EmployerRepEditModal from '../../employer/manage-users/EmployerRepEditModal';
import ConfirmationModal from '../../modals/ConfirmationModal';

// Modified by AI on 05/14/2026. Edit #1.
export const getUserMenuData = ({ user }) => {
    if (!user?.impersonation) {
        return {
            employerView: false,
            recruiterView: false,
            talenyaView: false,
        };
    }

    const { realEntityRole, impersonatedAsEmployer, impersonatedAsRecruiter } = user.impersonation;
    const employerView = realEntityRole === UserRoles.employerRep || impersonatedAsEmployer;
    const recruiterView = realEntityRole === UserRoles.recruiter || (!impersonatedAsEmployer && impersonatedAsRecruiter);
    const talenyaView = realEntityRole === UserRoles.talenya && !impersonatedAsEmployer && !impersonatedAsRecruiter;

    return {
        employerView,
        recruiterView,
        talenyaView,
    };
};

// Modified by AI on 05/13/2026. Edit #1.
export const getEntityInfo = ({ user, employer, recruiter }) => {
    const { employerView, recruiterView } = getUserMenuData({ user });

    if (employer && employerView) {
        return {
            logoUrl: employer.logoURL,
            label: 'Employer',
            name: employer.name,
        };
    }

    if (recruiter && recruiterView) {
        return {
            logoUrl: recruiter.photoURL,
            label: 'Recruiter',
            name: recruiter.fullName,
        };
    }

    return null;
};

export const getUserDetailsMenuItem = ({
    employer,
    showErrorNotification,
    showSuccessNotification,
    getAuthEntityId,
    showModal,
    hideModal,
}) => {
    if (!employer) {
        return [];
    }

    // window.location.href.endsWith('manageUsers') -- is used to hide modal on manage users page
    // sync is needed here, currently not implemented
    return {
        label: 'EMPLOYER_REP_DETAILS',
        testId: 'um-employer-rep-details',
        onClick: async () => {
            const currentEmployerRep = await EmployerRepHttpService.getEmployerRep(getAuthEntityId());

            const modalId = showModal({
                body: <EmployerRepEditModal
                    title={'EMPLOYER_REP_DETAILS'}
                    employerRep={currentEmployerRep}
                    onConfirm={async (updatedEmployerRep) => {
                        try {
                            await EmployerRepHttpService.updateEmployerRep(updatedEmployerRep);
                            showSuccessNotification('DEFAULT_NOTIFICATION_CHANGES_SAVED');
                            hideModal(modalId);
                        } catch (error) {
                            Logger.error(error.message);
                            if (includes(error.message, 'ALREADY_EXIST')) {
                                showErrorNotification('ADD_USER_EMAIL_ALREADY_EXIST');
                            } else {
                                showErrorNotification('DEFAULT_NOTIFICATION_ERROR');
                            }
                        }
                    }}
                    onCancel={() => {
                        hideModal(modalId);
                    }}
                    isEditMode={true}
                />,
            });
        },

    };
};

// Modified by AI on 05/14/2026. Edit #1.
const useActivationMenuItem = ({
    employer,
    history,
    clickHandlerWrapper = (cb) => () => cb(),
}) => {
    const { showModal, hideModal } = useContext(ModalContext);
    const { showErrorNotification, showSuccessNotification } = useContext(NotificationContext);

    const status = get(employer, 'status.current', '');
    const isActiveEmployer = ['Active'].includes(status);
    const isInactiveEmployer = ['Pending', 'Inactive'].includes(status);

    if (isActiveEmployer) {
        return {
            label: 'DEACTIVATE',
            testId: 'um-deactivate',
            onClick: clickHandlerWrapper(async () => {
                const modalId = showModal({
                    body: <ConfirmationModal
                        message={'DEACTIVATE_EMPLOYER_MESSAGE'}
                        title={''}
                        confirmButtonLabel={'DEACTIVATE_EMPLOYER_CONFIRMATION'}
                        rejectButtonLabel={'CANCEL'}
                        onConfirmButtonClick={() => {
                            EmployerHttpService.deactivateEmployer(employer._id)
                                .then(() => {
                                    showSuccessNotification('DEFAULT_NOTIFICATION_CHANGES_SAVED');
                                })
                                .catch((error) => {
                                    Logger.error(error);
                                    showErrorNotification('DEACTIVATE_EMPLOYER_ERROR');
                                })
                                .finally(() => {
                                    history.push(`/employer/${employer._id}/positions`);
                                    hideModal(modalId);
                                });
                        }}
                        onRejectButtonClick={() => hideModal(modalId)}
                    />,
                });
            }),
        };
    }

    if (isInactiveEmployer) {
        return {
            label: 'ACTIVATE',
            testId: 'um-activate',
            onClick: clickHandlerWrapper(async () => {
                try {
                    await EmployerHttpService.activateEmployer(employer._id);

                    history.push(`/employer/${employer._id}/positions`);
                } catch (error) {
                    Logger.error(error);
                    showErrorNotification('ACTIVATE_EMPLOYER_ERROR');
                }
            }),
        };
    }

    return null;
};

export const getActivationMenuItem = useActivationMenuItem;

// Modified by AI on 05/14/2026. Edit #3.
const useEmployerMenuItems = ({
    user,
    employer,
    settings,
    history,
}) => {
    const {
        isEmployerRepAdmin, getAuthEntityId, logout, isUserAllowedToCreateRecruiters,
    } = useContext(AuthorizationContext);
    const { showErrorNotification, showSuccessNotification } = useContext(NotificationContext);
    const { showModal, hideModal } = useContext(ModalContext);
    const { systemPreferences } = useContext(SettingsContext);

    const newAccountDetailsWhitelist = systemPreferences?.newAccountDetailsWhitelist || [];
    const isNewAccountDetailsEnabled = newAccountDetailsWhitelist.includes(employer?._id);

    const { TFTAppUrl } = settings || {};
    const activationMenuItem = getActivationMenuItem({
        employer,
        history,
    });

    if (!user?.impersonation || !employer) {
        return [];
    }

    const { realEntityRole } = user.impersonation;
    const isTalenyaUser = realEntityRole === UserRoles.talenya;
    const isTEUser = realEntityRole === UserRoles.recruiter;
    const isEmployerUser = UserRoles.employerRep === realEntityRole;

    const isResellerEmployer = employer?.settings?.isResellerPartner;

    const isPcEmployer = employer?.isPaycorEmployer();

    const isCampaignsAnalysisPageEnabled = get(employer, 'settings.isCampaignsAnalysisPageEnabled');

    const menuItems = [];

    if (isEmployerUser && !window.location.href.endsWith('manageUsers')) {
        menuItems.push(getUserDetailsMenuItem({
            employer,
            showErrorNotification,
            showSuccessNotification,
            getAuthEntityId,
            showModal,
            hideModal,
        }));
    }

    menuItems.push({
        label: 'ACCOUNT_DETAILS',
        testId: 'um-account-details',
        onClick: () => {
            if (isNewAccountDetailsEnabled) {
                history.push(RoutePaths.editEmployer.replace(':employerId', employer._id));
                return;
            }
            window.location.href = `${TFTAppUrl}/#/employer/${employer._id}/editEmployer/${employer._id}`;
        },
    });

    if (isEmployerUser || isTalenyaUser || isTEUser) {
        menuItems.push({
            disabled: false,
            label: 'COMPANY_CAMPAIGNS_MENU_ITEM',
            testId: 'company-campaigns',
            onClick: () => {
                history.push(`/employer/${employer._id}/employerCampaigns`);
            },
        });
    }

    if (isEmployerUser || isTalenyaUser || isTEUser) {
        menuItems.push({
            disabled: !isCampaignsAnalysisPageEnabled,
            label: 'CAMPAIGNS_ACTIVITY_EMPLOYER_MENU_SUBITEM',
            testId: 'um-campaigns-activity',
            onClick: () => {
                history.push(`/employer/${employer._id}/campaignsActivity`);
            },
        });
    }

    if (isPcEmployer) {
        menuItems.push({
            label: 'CAMPAIGNS_ACTIVITY_EMPLOYER_MENU_PSS_REPORT',
            testId: 'um-pss-campaign-report',
            onClick: () => {
                history.push(`/employer/${employer._id}/pcCampaignsActivity`);
            },
        });
    }

    if (isResellerEmployer && (user?.permissions?.isTalenyaUser || user?.permissions?.isTalentExpertUser)) {
        menuItems.push({
            label: 'CREATE_RESELLER_ASSOCIATED_EMPLOYER',
            testId: 'um-create-associated-employer',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/employer/${employer?._id}/createAssociatedEmployer`;
            },
        });
    }

    if ((isEmployerUser && isEmployerRepAdmin) || isTalenyaUser || isTEUser) {
        menuItems.push({
            label: 'MANAGE_USERS',
            testId: 'um-manage-users',
            onClick: () => {
                history.push(`/employer/${employer._id}/manageUsers`);
            },
        });
    }

    // Modified by AI on 10/27/2026. Edit #4.
    if (isTalenyaUser && isUserAllowedToCreateRecruiters) {
        menuItems.push({
            label: 'CREATE_TALENT_EXPERT',
            testId: 'um-create-talent-expert',
            onClick: () => {
                history.push(RoutePaths.talenyaCreateRecruiter);
            },
        });
    }

    if (isTalenyaUser && activationMenuItem) {
        menuItems.push(activationMenuItem);
    }

    menuItems.push({
        label: 'LOGOUT',
        testId: 'um-logout',
        onClick: async () => {
            await logout();
        },
    });

    return menuItems;
};

export const getEmployerMenuItems = useEmployerMenuItems;

// Modified by AI on 05/14/2026. Edit #3.
const useRecruiterMenuItems = ({
    user,
    recruiter,
    settings,
    employer,
}) => {
    const { logout } = useContext(AuthorizationContext);
    const { TFTAppUrl } = settings || {};

    if (!user?.impersonation || !recruiter) {
        return [];
    }

    const isResellerEmployer = employer?.settings?.isResellerPartner;

    const isNeedToShowAssociatedEmployerItem = isResellerEmployer && (user?.permissions?.isTalenyaUser || user?.permissions?.isTalentExpertUser);

    return [
        {
            label: 'ACCOUNT_DETAILS',
            testId: 'um-account-details',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/recruiter/${recruiter._id}/editRecruiter/${recruiter._id}`;
            },
        },
        ...(isNeedToShowAssociatedEmployerItem ? [{
            label: 'CREATE_RESELLER_ASSOCIATED_EMPLOYER',
            testId: 'um-create-associated-employer',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/employer/${employer?._id}/createAssociatedEmployer`;
            },
        }] : []),
        {
            label: 'LOGOUT',
            testId: 'um-logout',
            onClick: async () => {
                await logout();
            },
        }];
};

export const getRecruiterMenuItems = useRecruiterMenuItems;

// Modified by AI on 05/14/2026. Edit #5.
const useTalenyaMenuItems = ({
    user,
    settings,
    employer,
    history,
}) => {
    // Modified by AI on 10/27/2026. Edit #6.
    const { logout, isUserAllowedToCreateRecruiters } = useContext(AuthorizationContext);
    const { showSuccessNotification, showErrorNotification } = useContext(NotificationContext);

    if (!user?.impersonation) {
        return [];
    }

    const {
        TFTAppUrl,
    } = settings || {};

    const isResellerEmployer = employer?.settings?.isResellerPartner;

    const isNeedToShowAssociatedEmployerItem = isResellerEmployer && (user?.permissions?.isTalenyaUser || user?.permissions?.isTalentExpertUser);

    return [
        {
            label: 'REFRESH_LISTS',
            testId: 'um-refresh-list',
            onClick: async () => {
                try {
                    await TalenyaHttpService.getRefreshMetaData();

                    showSuccessNotification('LIST_REFRESHED_SUCCESS');
                } catch (error) {
                    Logger.error(error);
                    showErrorNotification('LIST_REFRESHED_ERROR');
                }
            },
        },
        ...(isUserAllowedToCreateRecruiters ? [{
            label: 'CREATE_TALENT_EXPERT',
            testId: 'um-create-talent-expert',
            onClick: () => {
                history.push(RoutePaths.talenyaCreateRecruiter);
            },
        }] : []),
        {
            label: 'CREATE_EMPLOYER',
            testId: 'um-create-employer',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/talenya/createNewEmployer`;
            },
        },
        ...(isNeedToShowAssociatedEmployerItem ? [{
            label: 'CREATE_RESELLER_ASSOCIATED_EMPLOYER',
            testId: 'um-create-associated-employer',
            onClick: () => {
                window.location.href = `${TFTAppUrl}/#/employer/${employer?._id}/createAssociatedEmployer`;
            },
        }] : []),
        {
            label: 'BLOCK_ACTIVE_SESSIONS',
            testId: 'um-drop-all-sessions',
            onClick: async () => {
                try {
                    await TalenyaHttpService.blockActiveSessions();
                    await logout();
                } catch (error) {
                    Logger.error(error);
                    showErrorNotification('BLOCK_ACTIVE_SESSIONS_ERROR');
                }
            },
        },
        {
            label: 'CLEAR_ALL_CACHES',
            testId: 'um-clear-all-caches',
            onClick: async () => {
                try {
                    await TalenyaHttpService.clearAllCaches();

                    showSuccessNotification('CLEAR_ALL_CACHES_SUCCESS');
                } catch (error) {
                    Logger.error(error);
                    showErrorNotification('CLEAR_ALL_CACHES_ERROR');
                }
            },
        },
        {
            label: 'LOGOUT',
            testId: 'um-logout',
            onClick: async () => {
                await logout();
            },
        },
    ];
};

export const getTalenyaMenuItems = useTalenyaMenuItems;

// Modified by AI on 05/14/2026. Edit #4.
export const useMenuItems = ({
    user,
    employer,
    recruiter,
    settings,
    history,
}) => {
    const { employerView, recruiterView, talenyaView } = getUserMenuData({ user });

    // All hook-based builders must be called unconditionally to satisfy Rules of Hooks
    const employerMenuItems = useEmployerMenuItems({
        user, employer, settings, history,
    });
    const recruiterMenuItems = useRecruiterMenuItems({
        user, employer, settings, recruiter,
    });
    const talenyaMenuItems = useTalenyaMenuItems({
        user, employer, settings, history,
    });

    if (employerView) return employerMenuItems;
    if (recruiterView) return recruiterMenuItems;
    if (talenyaView) return talenyaMenuItems;

    return [];
};

export const getMenuItems = useMenuItems;

// Modified by AI on 05/14/2026. Edit #2.
export const useUserMenuItems = (menuItemsProps) => useMenuItems(menuItemsProps);
